import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import { MdKeyboardDoubleArrowLeft, MdKeyboardDoubleArrowRight } from "react-icons/md";
import { HiCheck } from "react-icons/hi";
import { FiUploadCloud, FiFileText } from "react-icons/fi";
//
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import InputComponent from "../../../components/Tenant/InputComponent/InputComponent";
import SelectComponentWithSearch from "../../../components/Tenant/SelectComponentWIthSearch/SelectComponentWithSearch";
import MultiSelectComponent from "../../../components/Tenant/MultiSelectComponent/MultiSelectComponent";
import ModalComponent from "../../../components/Tenant/ModalComponent/ModalComponent";
//
import { useGetAppUserTypesMutation } from "../../../features/tenant/appUsers/appUsersApi";
import { useFetchCatalogueByTypeMutation } from "../../../features/tenant/giftCatalogue/giftCatalogueApi";
import { useFetchHeirarchyMutation } from "../../../features/tenant/productHeirarchy/productHeirarchyApi";
import {
  useFetchProductClassificationListMutation,
  useFetchProductsMutation,
  useCountProductsMutation,
  useFetchProductsByCategoryIdMutation,
} from "../../../features/tenant/products/productsApi";
import { useUploadSingleFileMutation } from "../../../features/admin/imageUpload/imageUploadApi";
import {
  useCreateSchemeMutation,
  useUpdateSchemeByIdMutation,
  useFetchSchemeSkuPointsMutation,
} from "../../../features/tenant/scheme/schemeApi";
//
import { getCurrentDate } from "../../../utils/getCurrentDate";
import { getVendorName } from "../../../utils/getVendorName";
import { States } from "../../../utils/getStates";
import { egyptStates, getMatchingEgyptStates, latoStates, getMatchingLatoStates } from "../../../utils/ultimatrueStates";
import TenantSlugs from "../../../utils/TenantSlug";
import { validateSchemeParserExpression } from "../../../utils/schemeParserValidator";
import { buildSchemeSettings } from "./schemeSettings";
import { listDrafts, saveDraft, deleteDraft } from "./schemeDrafts";
import ProductCodeUpload from "./ProductCodeUpload";
import SkuPointsPanel from "./SkuPointsPanel";
import SchemeReminders, { emptyReminders, normalizeReminders, validateReminders, formatReminders } from "./SchemeReminders";
import { startDay, endDay } from "./schemeDates";

const TIER_TYPES = [
  { id: "1", name: "Points" },
  { id: "2", name: "Weight" },
];

const ALL_SCHEME_TYPES = [
  { id: 1, name: "All Products" },
  { id: 2, name: "Classification" },
  { id: 3, name: "Subcategory-Category" },
];

const STANDARD_KEY_VALUES = new Set(["qr_mrp", "product_mrp", "product_points", "qr_original_mrp", "qr_m_points", "sku_points"]);

const KEY_OPTIONS = [
  { value: "qr_mrp", label: "QR MRP", desc: "qr.mrp" },
  { value: "product_mrp", label: "Product MRP", desc: "products.mrp" },
  { value: "product_points", label: "Product Points", desc: "products.{userType}_points" },
  { value: "qr_original_mrp", label: "QR Original MRP", desc: "qr.original_mrp" },
  { value: "qr_m_points", label: "QR Membership Points", desc: "qr.m_points" },
  { value: "sku_points", label: "SKU-wise Points", desc: "this scheme · per SKU", needsSpecificProducts: true },
  // { value: "__custom__", label: "Custom column…", desc: "Any extra DB column" },
];

const GIFT_TYPE_OPTIONS = [
  { id: "cash", name: "cash" },
  { id: "gift", name: "gift" },
  { id: "gift_as_cashback", name: "gift_as_cashback" },
];

const STEPS = [
  { label: "Info", desc: "Name, eligibility, schedule and media." },
  { label: "Filters & Products", desc: "Where the scheme applies and which products qualify." },
  { label: "Points & Rewards", desc: "Reward catalogue and optional per-user-type point formulas." },
  { label: "Redemption", desc: "When users can redeem, cashPerPoint limits, and TDS handling." },
  { label: "Review", desc: "Confirm everything before creating the scheme." },
];

const inputClass =
  "block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all";

function generateProductHeirarchyDropdownOptions(data) {
  const subcategoryMapping = {};
  data.forEach((item) => {
    if (item.is_sub === 1 && item.is_master === 0) {
      const parentCategoryName = data.find((c) => c.id === item.master_id)?.name;
      if (parentCategoryName) {
        if (!subcategoryMapping[parentCategoryName]) subcategoryMapping[parentCategoryName] = [];
        subcategoryMapping[parentCategoryName].push({ name: item.name, id: item.id });
      }
    }
  });
  const options = [];
  for (const parentCategory in subcategoryMapping) {
    subcategoryMapping[parentCategory].forEach((sub) => {
      options.push({ id: sub.id, name: `${sub.name} : ${parentCategory}` });
    });
  }
  return options;
}

// ---------- presentational primitives ----------

const ToggleSwitch = ({ label, checked, onChange, hint }) => (
  <div className="flex items-start gap-3 select-none">
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
        checked ? "bg-blue-600" : "bg-gray-300"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </button>
    {(label || hint) && (
      <div className="cursor-pointer" onClick={() => onChange(!checked)}>
        {label && <span className="block text-sm font-medium text-gray-800">{label}</span>}
        {hint && <span className="block text-xs text-gray-500 mt-0.5">{hint}</span>}
      </div>
    )}
  </div>
);

const SectionCard = ({ title, desc, children }) => (
  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-8">
    {(title || desc) && (
      <div className="mb-6 pb-4 border-b border-gray-100">
        {title && <h2 className="text-lg font-semibold text-gray-900">{title}</h2>}
        {desc && <p className="text-sm text-gray-500 mt-1">{desc}</p>}
      </div>
    )}
    {children}
  </div>
);

const DateField = ({ label, value, onChange, required }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input type="date" className={inputClass} value={value} onChange={(e) => onChange(e.target.value)} />
  </div>
);

// positive: no sign/exponent keys; zero and negatives are rejected by the wizard validation
const NumberField = ({ label, value, onChange, required, hint, placeholder, disabled, positive }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type="number"
      min={positive ? "0.01" : "0"}
      step="0.01"
      disabled={disabled}
      className={`${inputClass} ${disabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""}`}
      value={disabled ? "" : value}
      placeholder={placeholder}
      onKeyDown={positive ? (e) => ["-", "+", "e", "E"].includes(e.key) && e.preventDefault() : undefined}
      onChange={(e) => onChange(e.target.value)}
    />
    {hint && <p className={`text-xs mt-1 ${disabled ? "text-amber-600" : "text-gray-500"}`}>{hint}</p>}
  </div>
);

const FileDrop = ({ label, accept, onChange, value, kind }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <label className="flex flex-col items-center justify-center gap-1 border-2 border-dashed border-gray-200 rounded-xl px-3 py-5 cursor-pointer hover:border-blue-400 hover:bg-blue-50/40 transition-colors text-center">
      <FiUploadCloud className="h-5 w-5 text-gray-400" />
      <span className="text-xs text-gray-500">{value ? "Replace file" : "Click to upload"}</span>
      <input type="file" accept={accept} className="hidden" onChange={onChange} />
    </label>
    {value && kind === "image" && <img src={value} alt={label} className="mt-2 h-32 w-32 object-cover rounded-lg border border-gray-200" />}
    {value && kind === "pdf" && (
      <a href={value} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100 transition-colors">
        <FiFileText className="h-4 w-4" /> View PDF
      </a>
    )}
  </div>
);

const WizardStepper = ({ steps, current, onStep }) => (
  <div className="flex items-center">
    {steps.map((s, i) => {
      const done = i < current;
      const active = i === current;
      return (
        <div key={s.label} className={`flex items-center ${i < steps.length - 1 ? "flex-1" : ""}`}>
          <button type="button" onClick={() => onStep(i)} className="flex items-center gap-2 shrink-0 group">
            <span
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-all ${
                active
                  ? "bg-blue-600 text-white ring-4 ring-blue-100"
                  : done
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-400 border border-gray-300 group-hover:border-gray-400"
              }`}
            >
              {done ? <HiCheck className="h-4 w-4" /> : i + 1}
            </span>
            <span className={`text-sm hidden md:block ${active ? "font-semibold text-gray-900" : done ? "font-medium text-gray-600" : "text-gray-400"}`}>
              {s.label}
            </span>
          </button>
          {i < steps.length - 1 && <div className={`flex-1 h-0.5 mx-2 md:mx-3 rounded ${done ? "bg-blue-600" : "bg-gray-200"}`} />}
        </div>
      );
    })}
  </div>
);

// Multi-select for states that also lets the user CREATE a custom state by typing
// (e.g. "Western UP"). Picking "All" expands to every actual state (not the string "All").
const StateMultiSelect = ({ options, selected, setSelected }) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const norm = (s) => (s || "").toLowerCase().trim();
  const isSelected = (nm) => selected.some((s) => norm(s.name) === norm(nm));
  const realStates = options.filter((o) => o.id !== "ALL");

  const filtered = options.filter(
    (o) => o.name.toLowerCase().includes(query.toLowerCase()) && (o.id === "ALL" || !isSelected(o.name)),
  );
  const canCreate =
    query.trim() &&
    !options.some((o) => norm(o.name) === norm(query)) &&
    !isSelected(query);

  const selectAll = () => {
    const merged = [...selected];
    realStates.forEach((s) => {
      if (!merged.some((x) => norm(x.name) === norm(s.name))) merged.push(s);
    });
    setSelected(merged);
    setQuery("");
  };
  const addOption = (o) => {
    if (o.id === "ALL") {
      selectAll();
      return;
    }
    if (!isSelected(o.name)) setSelected([...selected, o]);
    setQuery("");
  };
  const addTyped = () => {
    const c = query.trim();
    if (!c || isSelected(c)) {
      setQuery("");
      return;
    }
    setSelected([...selected, { id: c, name: c }]);
    setQuery("");
  };
  const remove = (nm) => setSelected(selected.filter((s) => norm(s.name) !== norm(nm)));

  return (
    <div className="w-full">
      <div className="flex items-center gap-2 mb-1">
        <label className="block text-sm font-medium text-gray-700">
          States <span className="text-red-500">*</span>
        </label>
        <button type="button" onClick={selectAll} className="text-xs text-blue-600 hover:underline">Select all</button>
        {selected.length > 0 && (
          <button type="button" onClick={() => setSelected([])} className="text-xs text-gray-400 hover:text-gray-600">Clear</button>
        )}
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selected.map((s) => (
            <span key={s.id} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs px-2.5 py-1 rounded-full border border-blue-100 capitalize">
              {s.name}
              <button type="button" onClick={() => remove(s.name)} className="font-bold leading-none hover:text-blue-900">×</button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <input
          className={inputClass}
          placeholder="Type to search, add a custom state (e.g. Western UP), or pick All"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTyped(); } }}
        />
        {open && (query || filtered.length > 0) && (
          <div className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 text-sm">
            {canCreate && (
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={addTyped} className="block w-full text-left px-3 py-2 hover:bg-blue-50 text-blue-700 font-medium">
                + Add “{query.trim()}”
              </button>
            )}
            {filtered.slice(0, 100).map((o) => (
              <button
                key={o.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => addOption(o)}
                className={`block w-full text-left px-3 py-2 hover:bg-gray-100 capitalize ${o.id === "ALL" ? "font-medium text-blue-700" : ""}`}
              >
                {o.id === "ALL" ? "Select all states" : o.name}
              </button>
            ))}
            {filtered.length === 0 && !canCreate && <div className="px-3 py-2 text-gray-500">No matches</div>}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- formula builder helpers ----------

const LEAF_TYPES = [
  { value: "pct", label: "Percentage", placeholder: "10", suffix: "% of key" },
  { value: "mul", label: "Multiplier", placeholder: "0.5", suffix: "× key" },
  { value: "flat", label: "Flat pts", placeholder: "50", suffix: "pts" },
];

function buildLeafExpr(type, val) {
  const n = val === "" ? "0" : val;
  if (type === "pct") return `${n}%`;
  if (type === "mul") return `key * ${n}`;
  return n; // flat
}

function deriveFormula({ formulaType, pct, mulFactor, flatPts, condOp, condThreshold, thenType, thenVal, elseType, elseVal }) {
  if (formulaType === "pct") return `${pct || "0"}%`;
  if (formulaType === "mul") return `key * ${mulFactor || "0"}`;
  if (formulaType === "flat") return flatPts || "0";
  if (formulaType === "conditional") {
    return `if(key ${condOp} ${condThreshold || "0"}) ? ${buildLeafExpr(thenType, thenVal)} : ${buildLeafExpr(elseType, elseVal)}`;
  }
  return "";
}

function parseFormulaToBuilder(parser) {
  if (!parser || !parser.trim()) return null;
  const t = parser.trim();
  const pct = t.match(/^([\d.]+)%$/);
  if (pct) return { formulaType: "pct", pct: pct[1] };
  const mul = t.match(/^key\s*\*\s*([\d.]+)$/);
  if (mul) return { formulaType: "mul", mulFactor: mul[1] };
  const flat = t.match(/^([\d.]+)$/);
  if (flat) return { formulaType: "flat", flatPts: flat[1] };
  const cond = t.match(/^if\(key\s*(>=|<=|!=|==|>|<)\s*([\d.]+)\)\s*\?\s*(.+?)\s*:\s*(.+)$/);
  if (cond) {
    const parseBranch = (expr) => {
      const p = expr.match(/^([\d.]+)%$/);
      if (p) return { type: "pct", val: p[1] };
      const m = expr.match(/^key\s*\*\s*([\d.]+)$/);
      if (m) return { type: "mul", val: m[1] };
      const f = expr.match(/^([\d.]+)$/);
      if (f) return { type: "flat", val: f[1] };
      return null;
    };
    const thenB = parseBranch(cond[3]);
    const elseB = parseBranch(cond[4]);
    if (thenB && elseB)
      return { formulaType: "conditional", condOp: cond[1], condThreshold: cond[2], thenType: thenB.type, thenVal: thenB.val, elseType: elseB.type, elseVal: elseB.val };
  }
  return null;
}

function evalForPreview(parser, keyValue) {
  try {
    // safe re-use of the validator's internal evaluator
    const t = parser.trim();
    if (t.startsWith("if(")) {
      const m = t.match(/^if\(key\s*(>=|<=|!=|==|>|<)\s*([\d.]+)\)\s*\?\s*(.+?)\s*:\s*(.+)$/);
      if (!m) return null;
      const op = m[1], th = parseFloat(m[2]);
      const ops = { ">": (a,b)=>a>b, "<": (a,b)=>a<b, ">=": (a,b)=>a>=b, "<=": (a,b)=>a<=b, "==": (a,b)=>a===b, "!=": (a,b)=>a!==b };
      const branch = ops[op]?.(keyValue, th) ? m[3].trim() : m[4].trim();
      return evalLeaf(branch, keyValue);
    }
    return evalLeaf(t, keyValue);
  } catch { return null; }
}

function evalLeaf(expr, kv) {
  const p = expr.match(/^\(?([\d.]+)%\)?$/);
  if (p) return Math.round((parseFloat(p[1]) * kv) / 100 * 100) / 100;
  const m = expr.match(/^\(?key\s*\*\s*([\d.]+)\)?$/);
  if (m) return Math.round(kv * parseFloat(m[1]) * 100) / 100;
  if (expr === "key") return kv;
  const n = expr.match(/^\(?([\d.]+)\)?$/);
  if (n) return parseFloat(n[1]);
  return null;
}

const LeafPicker = ({ type, value, onTypeChange, onValueChange }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <select value={type} onChange={(e) => onTypeChange(e.target.value)} className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/40">
      {LEAF_TYPES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
    </select>
    <input
      type="number"
      min="0"
      step={type === "mul" ? "0.01" : "1"}
      value={value}
      onChange={(e) => onValueChange(e.target.value)}
      className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 w-24 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/40"
      placeholder={LEAF_TYPES.find((l) => l.value === type)?.placeholder}
    />
    <span className="text-[11px] text-gray-400">{LEAF_TYPES.find((l) => l.value === type)?.suffix}</span>
  </div>
);

const FormulaBuilder = ({ parser, onChange }) => {
  const parsed = parseFormulaToBuilder(parser);
  const canUseBuilder = !!parsed || !parser.trim();

  const [rawMode, setRawMode] = useState(!canUseBuilder);
  const [sampleValue, setSampleValue] = useState("1000");

  const [formulaType, setFormulaType] = useState(parsed?.formulaType || "pct");
  const [pct, setPct] = useState(parsed?.pct || "10");
  const [mulFactor, setMulFactor] = useState(parsed?.mulFactor || "0.1");
  const [flatPts, setFlatPts] = useState(parsed?.flatPts || "50");
  const [condOp, setCondOp] = useState(parsed?.condOp || ">=");
  const [condThreshold, setCondThreshold] = useState(parsed?.condThreshold || "500");
  const [thenType, setThenType] = useState(parsed?.thenType || "pct");
  const [thenVal, setThenVal] = useState(parsed?.thenVal || "15");
  const [elseType, setElseType] = useState(parsed?.elseType || "pct");
  const [elseVal, setElseVal] = useState(parsed?.elseVal || "10");

  const builderState = { formulaType, pct, mulFactor, flatPts, condOp, condThreshold, thenType, thenVal, elseType, elseVal };

  const emitBuilder = (patch) => {
    const next = { ...builderState, ...patch };
    onChange(deriveFormula(next));
  };

  const switchToBuilder = () => {
    const p = parseFormulaToBuilder(parser);
    if (!p && parser.trim()) return; // can't parse — stay in raw
    if (p) {
      setFormulaType(p.formulaType);
      if (p.pct) setPct(p.pct);
      if (p.mulFactor) setMulFactor(p.mulFactor);
      if (p.flatPts) setFlatPts(p.flatPts);
      if (p.condOp) setCondOp(p.condOp);
      if (p.condThreshold) setCondThreshold(p.condThreshold);
      if (p.thenType) setThenType(p.thenType);
      if (p.thenVal) setThenVal(p.thenVal);
      if (p.elseType) setElseType(p.elseType);
      if (p.elseVal) setElseVal(p.elseVal);
    }
    setRawMode(false);
  };

  const preview = sampleValue !== "" ? evalForPreview(parser, Number(sampleValue)) : null;
  const parserErr = parser ? validateSchemeParserExpression(parser) : { valid: true };
  const FORMULA_TYPES = [
    { value: "pct", label: "Percentage" },
    { value: "mul", label: "Multiplier" },
    { value: "flat", label: "Flat" },
    { value: "conditional", label: "Conditional" },
  ];

  return (
    <div className="mt-1">
      {/* mode tabs */}
      <div className="flex items-center gap-1 mb-3">
        <button type="button" onClick={switchToBuilder} className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${!rawMode ? "bg-blue-600 text-white" : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"}`}>
          Builder
        </button>
        <button type="button" onClick={() => setRawMode(true)} className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${rawMode ? "bg-blue-600 text-white" : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"}`}>
          Raw
        </button>
        {!rawMode && !canUseBuilder && parser.trim() && (
          <span className="text-[11px] text-amber-600 ml-1">Formula too complex for builder — switch to Raw to edit.</span>
        )}
      </div>

      {rawMode ? (
        <textarea
          rows={2}
          className={`${inputClass} font-mono text-xs resize-none ${parser && !parserErr.valid ? "border-red-400" : ""}`}
          placeholder="e.g. if(key >= 500) ? 10% : 5%"
          value={parser}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4">
          {/* formula type */}
          <div className="flex flex-wrap gap-1.5">
            {FORMULA_TYPES.map((ft) => (
              <button
                key={ft.value}
                type="button"
                onClick={() => { setFormulaType(ft.value); emitBuilder({ formulaType: ft.value }); }}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${formulaType === ft.value ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}
              >
                {ft.label}
              </button>
            ))}
          </div>

          {/* inputs */}
          {formulaType === "pct" && (
            <div className="flex items-center gap-3">
              <input type="number" min="0" max="100" step="0.1" value={pct} onChange={(e) => { setPct(e.target.value); emitBuilder({ pct: e.target.value }); }} className="text-sm border border-gray-300 rounded-lg px-3 py-2 w-28 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/40" />
              <span className="text-sm text-gray-500">% of the selected key value</span>
            </div>
          )}

          {formulaType === "mul" && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">key ×</span>
              <input type="number" min="0" step="0.01" value={mulFactor} onChange={(e) => { setMulFactor(e.target.value); emitBuilder({ mulFactor: e.target.value }); }} className="text-sm border border-gray-300 rounded-lg px-3 py-2 w-28 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/40" />
            </div>
          )}

          {formulaType === "flat" && (
            <div className="flex items-center gap-3">
              <input type="number" min="0" step="1" value={flatPts} onChange={(e) => { setFlatPts(e.target.value); emitBuilder({ flatPts: e.target.value }); }} className="text-sm border border-gray-300 rounded-lg px-3 py-2 w-28 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/40" />
              <span className="text-sm text-gray-500">fixed points (regardless of key value)</span>
            </div>
          )}

          {formulaType === "conditional" && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-gray-500 font-mono">if (key</span>
                <select value={condOp} onChange={(e) => { setCondOp(e.target.value); emitBuilder({ condOp: e.target.value }); }} className="border border-gray-300 rounded-lg px-2 py-1.5 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40">
                  {[">=", ">", "<=", "<", "==", "!="].map((op) => <option key={op}>{op}</option>)}
                </select>
                <input type="number" value={condThreshold} onChange={(e) => { setCondThreshold(e.target.value); emitBuilder({ condThreshold: e.target.value }); }} className="border border-gray-300 rounded-lg px-2 py-1.5 w-24 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40" />
                <span className="text-gray-500 font-mono">) ?</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-500 mb-1 font-medium">Then (condition true)</p>
                  <LeafPicker type={thenType} value={thenVal} onTypeChange={(v) => { setThenType(v); emitBuilder({ thenType: v }); }} onValueChange={(v) => { setThenVal(v); emitBuilder({ thenVal: v }); }} />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1 font-medium">Else (condition false)</p>
                  <LeafPicker type={elseType} value={elseVal} onTypeChange={(v) => { setElseType(v); emitBuilder({ elseType: v }); }} onValueChange={(v) => { setElseVal(v); emitBuilder({ elseVal: v }); }} />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* generated expression + validation */}
      {parser && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <code className="bg-gray-100 border border-gray-200 px-2 py-0.5 rounded text-[11px] text-gray-700 font-mono">{parser}</code>
          {parserErr.valid ? (
            <span className="text-[11px] text-green-600 flex items-center gap-1"><HiCheck className="h-3 w-3" /> valid</span>
          ) : (
            <span className="text-[11px] text-red-600">{parserErr.error}</span>
          )}
        </div>
      )}

      {/* live preview */}
      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-gray-500">
        <span>Preview with key =</span>
        <input
          type="number"
          value={sampleValue}
          onChange={(e) => setSampleValue(e.target.value)}
          className="border border-gray-300 rounded-lg px-2 py-1 w-24 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/40"
        />
        {preview !== null ? (
          <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-full font-medium">→ {preview} pts</span>
        ) : (
          parser.trim() && <span className="text-gray-400">—</span>
        )}
      </div>
    </div>
  );
};

// ---------- redemption config helpers ----------
// Backend: src/db/migrations/tenant/20260930120000_scheme_redemption_config.ts (scheme.redemption_config jsonb)
// Shape: { default: {...7 fields}, by_user_type: { "<user_type>": {...subset of the 7 fields} } }
// Keyed by the exact `user_type` string (not lowercased) — must match req.user.user_type on the backend.

const REDEMPTION_FIELD_KEYS = [
  "cash_per_point",
  "min_point_redeem",
  "min_cash_redeem",
  "max_amount_per_transaction",
  "max_amount_per_day",
  "max_transaction_per_day",
  "registration_bonus",
];

const REDEMPTION_FIELD_LABELS = {
  cash_per_point: "Rate",
  min_point_redeem: "Min pts",
  min_cash_redeem: "Min ₹",
  max_amount_per_transaction: "Max ₹/txn",
  max_amount_per_day: "Max ₹/day",
  max_transaction_per_day: "Max txns/day",
  registration_bonus: "Signup bonus",
};

// Canonical SKU-points rows: sorted by code, numeric values, empty cells dropped.
// Used for the save payload and for change detection against the loaded rows.
const normalizeSkuRows = (rows) =>
  (rows || [])
    .map((r) => {
      const points = {};
      Object.entries(r.points || {}).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined && Number.isFinite(Number(v))) points[k] = Number(v);
      });
      return { product_code: r.product_code, points };
    })
    .sort((a, b) => a.product_code.localeCompare(b.product_code));

const emptyRedemptionFields = () =>
  REDEMPTION_FIELD_KEYS.reduce((acc, k) => ({ ...acc, [k]: "" }), {});

// Minimum Cash per Redemption = conversion rate × minimum points. Any change to either input
// recomputes it, overwriting a manual value; typing into Minimum Cash directly is left as-is until then.
const applyAutoMinCash = (fields, field, val) => {
  const next = { ...fields, [field]: val };
  if (field === "cash_per_point" || field === "min_point_redeem") {
    const rate = Number(next.cash_per_point) || 0;
    const minPts = Number(next.min_point_redeem) || 0;
    next.min_cash_redeem = rate > 0 && minPts > 0 ? String(Math.round(rate * minPts * 100) / 100) : "";
  }
  return next;
};

const numOrUndef = (v) => {
  if (v === "" || v === null || v === undefined) return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
};

// Only include fields the admin actually filled in — a blank field means "no limit"
// on the backend, not zero.
const fieldsToConfig = (fields) => {
  const out = {};
  REDEMPTION_FIELD_KEYS.forEach((k) => {
    const n = numOrUndef(fields?.[k]);
    if (n !== undefined) out[k] = n;
  });
  return out;
};

const fmtRedemptionConfig = (cfg) => {
  if (!cfg || !cfg.default) return "Not configured — redemption is blocked for this scheme";
  const d = cfg.default;
  const parts = [];
  if (d.cash_per_point) parts.push(`₹${d.cash_per_point}/pt`);
  if (d.min_point_redeem) parts.push(`min ${d.min_point_redeem} pts`);
  if (d.min_cash_redeem) parts.push(`min ₹${d.min_cash_redeem}`);
  if (d.max_amount_per_transaction) parts.push(`max ₹${d.max_amount_per_transaction}/txn`);
  if (d.max_amount_per_day) parts.push(`max ₹${d.max_amount_per_day}/day`);
  if (d.max_transaction_per_day) parts.push(`max ${d.max_transaction_per_day} txns/day`);
  if (d.registration_bonus) parts.push(`+${d.registration_bonus} signup bonus`);
  const base = parts.length ? parts.join(", ") : "configured, no limits set";
  const overrideCount = Object.keys(cfg.by_user_type || {}).length;
  return overrideCount ? `${base} (+${overrideCount} user-type override${overrideCount > 1 ? "s" : ""})` : base;
};

// ---------- main ----------

const CreateSchemeWizard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Edit mode: SchemeList navigates to this wizard with the full scheme row in
  // location.state. No state => create mode.
  const editScheme = location.state?.id ? location.state : null;
  const isEdit = !!editScheme;
  // Clone mode: SchemeList navigates with { cloneFrom: row }. Treated as create.
  const cloneScheme = location.state?.cloneFrom || null;

  const vendorData = JSON.parse(sessionStorage.getItem("vendorData"));
  const tenantId = vendorData?.tenant_id;
  const isSeedworks = String(tenantId) === String(TenantSlugs.seedworks);
  const isUltimatrue = vendorData?.tenant_name === "ultimatrue";
  const isLato = vendorData?.tenant_name === "lato-milk";

  const allStates = isUltimatrue ? egyptStates : isLato ? latoStates : [{ id: "ALL", name: "All" }, ...States];

  // ---- step + draft state ----
  const [currentStep, setCurrentStep] = useState(0);
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [draftName, setDraftName] = useState("");
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [showDrafts, setShowDrafts] = useState(false);
  const [drafts, setDrafts] = useState([]);

  // ---- scheme fields ----
  const [name, setName] = useState("");
  const [applicableOn, setApplicableOn] = useState([]); // user type objects {id,name,user_type,user_type_id}
  const [selectedStates, setSelectedStates] = useState([]);
  const [startDate, setStartDate] = useState(getCurrentDate());
  const [endDate, setEndDate] = useState("");
  const [redeemStart, setRedeemStart] = useState(getCurrentDate());
  const [redeemEnd, setRedeemEnd] = useState("");
  const [images, setImages] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [detailImage, setDetailImage] = useState(null);
  const [description, setDescription] = useState("");
  const [isPrimary, setIsPrimary] = useState(false);

  const [selectedGiftCatalogue, setSelectedGiftCatalogue] = useState({ id: -1, name: "Select Catalogue" });
  const [selectedGiftTypes, setSelectedGiftTypes] = useState([]);
  const [selectedTierType, setSelectedTierType] = useState(TIER_TYPES[0]);
  const [userTypePoints, setUserTypePoints] = useState({}); // { utKey: { enabled, key, parser } }

  const [tdsCalc, setTdsCalc] = useState(false);
  const [tdsRedeem, setTdsRedeem] = useState(true);
  const [schemeUserCheck, setSchemeUserCheck] = useState(false);
  const [reminders, setReminders] = useState(emptyReminders()); // push reminders before scheme end / redeem end

  // Per-scheme cashPerPoint / redemption limits — independent of the global App User Type config.
  const [redemptionEnabled, setRedemptionEnabled] = useState(false);
  const [redemptionDefault, setRedemptionDefault] = useState(emptyRedemptionFields());
  const [redemptionByUserType, setRedemptionByUserType] = useState({}); // { [user_type]: { enabled, ...fields } }

  const [schemeType, setSchemeType] = useState({ id: -1, name: "Select Type" });
  const [selectedClassification, setSelectedClassification] = useState({ id: -1, name: "Select Classification" });
  const [selectedCategories, setSelectedCategories] = useState({ id: -1, name: "Select Subcategory" });
  const [selectedProducts, setSelectedProducts] = useState([]);
  // SKU-wise points for user types whose source field is "sku_points":
  // [{ product_code, points: { <userTypeKey>: number } }] — full replacement set on save.
  const [skuPointRows, setSkuPointRows] = useState([]);
  const originalSkuRowsRef = useRef([]); // as loaded for edit, for change detection

  // product pagination
  const [limit] = useState(15);
  const [offset, setOffset] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [productsArray, setProductsArray] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // dropdown sources
  const [allGiftCatalogues, setAllGiftCatalogues] = useState([]);
  const [userTypeOptions, setUserTypeOptions] = useState([]);
  const [classificationOptions, setClassificationOptions] = useState([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);

  // ---- API hooks ----
  const [getAppUserTypes, { data: appUserTypesData }] = useGetAppUserTypesMutation();
  const [fetchGiftCatalogues, { data: giftCataloguesData }] = useFetchCatalogueByTypeMutation();
  const [fetchHeirarchy, { data: heirarchyData }] = useFetchHeirarchyMutation();
  const [fetchSchemeSkuPoints] = useFetchSchemeSkuPointsMutation();
  const [fetchProductClassificationList, { data: classificationListData }] = useFetchProductClassificationListMutation();
  const [fetchProducts, { data: productsData }] = useFetchProductsMutation();
  const [countAllProducts, { data: countData }] = useCountProductsMutation();
  const [fetchProductsByCategoryId, { data: productsByCategoryData }] = useFetchProductsByCategoryIdMutation();
  const [uploadImage, { data: uploadImageData, error: uploadImageError, isLoading: uploadImageLoading }] = useUploadSingleFileMutation();
  const [uploadPdf, { data: uploadPdfData, error: uploadPdfError, isLoading: uploadPdfLoading }] = useUploadSingleFileMutation();
  const [uploadDetail, { data: uploadDetailData, error: uploadDetailError, isLoading: uploadDetailLoading }] = useUploadSingleFileMutation();
  const [addScheme, { data: addSchemeData, error: addSchemeError, isLoading: addSchemeLoading }] = useCreateSchemeMutation();
  const [updateScheme, { data: updateSchemeData, error: updateSchemeError, isLoading: updateSchemeLoading }] = useUpdateSchemeByIdMutation();

  const token = () => sessionStorage.getItem("access_token");

  // ---- initial fetches ----
  useEffect(() => {
    getAppUserTypes({ tenant_id: tenantId });
    fetchGiftCatalogues({ token: token(), tenant_id: tenantId, type: "3" });
    fetchHeirarchy({ token: token(), tenant_id: tenantId });
    fetchProductClassificationList({ token: token(), tenant_id: tenantId });
    setDrafts(listDrafts());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (appUserTypesData?.body) {
      setUserTypeOptions(appUserTypesData.body.map((u) => ({ ...u, id: u.user_type_id, name: u.user_type })));
    }
  }, [appUserTypesData]);

  useEffect(() => {
    if (giftCataloguesData?.body) setAllGiftCatalogues(giftCataloguesData.body);
  }, [giftCataloguesData]);

  useEffect(() => {
    if (heirarchyData?.body) setSubcategoryOptions(generateProductHeirarchyDropdownOptions(heirarchyData.body) || []);
  }, [heirarchyData]);

  useEffect(() => {
    if (classificationListData?.body) {
      const arr = [];
      classificationListData.body.forEach((each, idx) => {
        if (each.classification && each.classification.length > 0) arr.push({ id: idx, name: each.classification });
      });
      setClassificationOptions(arr);
    }
  }, [classificationListData]);

  // ---- product fetch logic ----
  const fetchProductsByClassification = (classification, product_code = "") => {
    setTotalPages(0);
    setProductsArray([]);
    const body = { limit, offset, classification, status: "1", product_code };
    fetchProducts({ token: token(), tenant_id: tenantId, body });
    countAllProducts({ token: token(), tenant_id: tenantId, body });
  };

  const fetchProductsByCatId = (subCatId, product_code = "") => {
    setTotalPages(0);
    setProductsArray([]);
    fetchProductsByCategoryId({ token: token(), tenant_id: tenantId, category_id: Number(subCatId), limit, offset, product_code });
  };

  useEffect(() => {
    if (selectedClassification.id !== -1 && searchQuery === "") fetchProductsByClassification(selectedClassification.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClassification, offset]);

  useEffect(() => {
    if (selectedCategories.id !== -1 && searchQuery === "") fetchProductsByCatId(selectedCategories.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategories, offset]);

  // reset product selection when the user changes scheme type.
  // Skip once right after loading a draft so restored products/classification aren't wiped.
  const applyingDraftRef = useRef(false);
  useEffect(() => {
    if (applyingDraftRef.current) {
      applyingDraftRef.current = false;
      return;
    }
    setOffset(0);
    setCurrentPage(0);
    setTotalPages(0);
    setProductsArray([]);
    setSelectedProducts([]);
    setSkuPointRows([]);
    setSelectedClassification({ id: -1, name: "Select Classification" });
    setSelectedCategories({ id: -1, name: "Select Subcategory" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemeType.id]);

  // ---- edit prefill ----
  // Runs once. Simple fields + settings come straight from the scheme row;
  // user types and gift catalogue are matched separately once their option
  // lists have loaded (async).
  // Bumped once after edit prefill applies, used as a `key` to remount the
  // headless multiselects so they reflect values set async/externally (they
  // don't re-sync their display on their own for a non-click value change).
  const [editSelectNonce, setEditSelectNonce] = useState(0);
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (!isEdit || prefilledRef.current) return;
    prefilledRef.current = true;
    const s = editScheme;
    setName(s.name || "");
    setStartDate(startDay(s.scheme_start) || getCurrentDate());
    setEndDate(endDay(s.scheme_end) || "");
    setRedeemStart(startDay(s.redeem_start) || getCurrentDate());
    setRedeemEnd(endDay(s.redeem_end) || "");
    setImages(s.image || null);
    setPdfFile(s.pdf || null);
    setDescription(s.description || "");
    setDetailImage(s.detail_image || s.detailImage || null);
    setIsPrimary(s.is_primary === true || s.is_primary === "true");
    setSelectedStates(
      isUltimatrue
        ? getMatchingEgyptStates(s.states || [])
        : isLato
        ? getMatchingLatoStates(s.states || [])
        : // Preserve every stored state name, including custom/non-canonical ones
          // (e.g. "Western UP") that aren't in the canonical States list, so they
          // aren't silently dropped on update.
          (s.states || []).map((nm) => States.find((st) => st.name === nm) || { id: nm, name: nm }),
    );

    const st = ALL_SCHEME_TYPES.find((t) => String(t.id) === String(s.type));
    if (st) {
      if (st.id !== schemeType.id) applyingDraftRef.current = true; // don't wipe seeded products
      setSchemeType(st);
    }
    // Seed already-selected products from their stored codes so they show as
    // chips and stay in the payload; new ones can be added/removed via search.
    if (Array.isArray(s.products) && s.products.length) {
      setSelectedProducts(s.products.map((code) => ({ product_id: code, product_code: code, name: "" })));
    }
    if (Array.isArray(s.gift_type)) {
      setSelectedGiftTypes(GIFT_TYPE_OPTIONS.filter((o) => s.gift_type.includes(o.id)));
    }
    const tt = TIER_TYPES.find((t) => String(t.id) === String(s.tier_type));
    if (tt) setSelectedTierType(tt);

    const settings = s.settings || {};
    setTdsCalc(settings.tds_calc ?? false);
    setTdsRedeem(settings.tds_redeem ?? true);
    setSchemeUserCheck(settings.type === "scheme_id_check");
    setReminders({ ...emptyReminders(), ...(settings.notifications || {}) });
    if (settings.user_type_points) {
      const utp = {};
      Object.entries(settings.user_type_points).forEach(([k, v]) => {
        if (v && typeof v === "object") {
          const isCustom = !STANDARD_KEY_VALUES.has(v.key || "qr_mrp");
          utp[k.toLowerCase()] = { enabled: true, key: v.key || "qr_mrp", parser: v.parser || "", isCustom, db_column: v.db_column || "", db_table: v.db_table || "products" };
        }
      });
      setUserTypePoints(utp);
    }

    const redemptionConfig = s.redemption_config || null;
    setRedemptionEnabled(!!redemptionConfig);
    setRedemptionDefault({ ...emptyRedemptionFields(), ...(redemptionConfig?.default || {}) });
    if (redemptionConfig?.by_user_type) {
      const byUt = {};
      Object.entries(redemptionConfig.by_user_type).forEach(([k, v]) => {
        if (v && typeof v === "object") byUt[k] = { enabled: true, ...emptyRedemptionFields(), ...v };
      });
      setRedemptionByUserType(byUt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit || userTypeOptions.length === 0) return;
    const ids = (editScheme.user_types || []).map(String);
    setApplicableOn(userTypeOptions.filter((u) => ids.includes(String(u.user_type_id))));
    setEditSelectNonce((n) => n + 1); // force multiselects to reflect prefilled values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, userTypeOptions]);

  useEffect(() => {
    if (!isEdit || allGiftCatalogues.length === 0) return;
    const cat = allGiftCatalogues.find((c) => String(c.id) === String(editScheme.gift_catalogue_id));
    if (cat) setSelectedGiftCatalogue(cat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, allGiftCatalogues]);

  // Existing SKU-wise points rows (edit: also kept as the baseline for change detection;
  // clone: copied into the new scheme).
  const skuRowsLoadedRef = useRef(false);
  useEffect(() => {
    const src = editScheme || cloneScheme;
    if (!src || skuRowsLoadedRef.current) return;
    const usesSku = Object.values(src.settings?.user_type_points || {}).some((c) => c?.key === "sku_points");
    if (!usesSku) return;
    skuRowsLoadedRef.current = true;
    fetchSchemeSkuPoints({ token: token(), tenant_id: tenantId, id: src.id })
      .unwrap()
      .then((res) => {
        const rows = normalizeSkuRows(res?.body || []);
        if (editScheme) originalSkuRowsRef.current = rows;
        setSkuPointRows(rows);
      })
      .catch(() => toast.error("Could not load the scheme's SKU-wise points"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, !!cloneScheme]);

  // Clone prefill — mirrors edit prefill exactly, except name gets "Copy of" prefix and isPrimary resets
  const clonePrefilledRef = useRef(false);
  useEffect(() => {
    if (!cloneScheme || clonePrefilledRef.current) return;
    clonePrefilledRef.current = true;
    const s = cloneScheme;
    setName(`Copy of ${s.name || ""}`);
    setStartDate(startDay(s.scheme_start) || getCurrentDate());
    setEndDate(endDay(s.scheme_end) || "");
    setRedeemStart(startDay(s.redeem_start) || getCurrentDate());
    setRedeemEnd(endDay(s.redeem_end) || "");
    setImages(s.image || null);
    setPdfFile(s.pdf || null);
    setDescription(s.description || "");
    setDetailImage(s.detail_image || s.detailImage || null);
    setIsPrimary(false);
    setSelectedStates(
      isUltimatrue
        ? getMatchingEgyptStates(s.states || [])
        : isLato
        ? getMatchingLatoStates(s.states || [])
        : (s.states || []).map((nm) => States.find((st) => st.name === nm) || { id: nm, name: nm }),
    );
    const st = ALL_SCHEME_TYPES.find((t) => String(t.id) === String(s.type));
    if (st) {
      if (st.id !== schemeType.id) applyingDraftRef.current = true;
      setSchemeType(st);
    }
    if (Array.isArray(s.products) && s.products.length) {
      setSelectedProducts(s.products.map((code) => ({ product_id: code, product_code: code, name: "" })));
    }
    if (Array.isArray(s.gift_type)) {
      setSelectedGiftTypes(GIFT_TYPE_OPTIONS.filter((o) => s.gift_type.includes(o.id)));
    }
    const tt = TIER_TYPES.find((t) => String(t.id) === String(s.tier_type));
    if (tt) setSelectedTierType(tt);
    const settings = s.settings || {};
    setTdsCalc(settings.tds_calc ?? false);
    setTdsRedeem(settings.tds_redeem ?? true);
    setSchemeUserCheck(settings.type === "scheme_id_check");
    setReminders({ ...emptyReminders(), ...(settings.notifications || {}) });
    if (settings.user_type_points) {
      const utp = {};
      Object.entries(settings.user_type_points).forEach(([k, v]) => {
        if (v && typeof v === "object") {
          const isCustom = !STANDARD_KEY_VALUES.has(v.key || "qr_mrp");
          utp[k.toLowerCase()] = { enabled: true, key: v.key || "qr_mrp", parser: v.parser || "", isCustom, db_column: v.db_column || "", db_table: v.db_table || "products" };
        }
      });
      setUserTypePoints(utp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!cloneScheme]);

  useEffect(() => {
    if (!cloneScheme || userTypeOptions.length === 0) return;
    const ids = (cloneScheme.user_types || []).map(String);
    setApplicableOn(userTypeOptions.filter((u) => ids.includes(String(u.user_type_id))));
    setEditSelectNonce((n) => n + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!cloneScheme, userTypeOptions]);

  useEffect(() => {
    if (!cloneScheme || allGiftCatalogues.length === 0) return;
    const cat = allGiftCatalogues.find((c) => String(c.id) === String(cloneScheme.gift_catalogue_id));
    if (cat) setSelectedGiftCatalogue(cat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!cloneScheme, allGiftCatalogues]);

  useEffect(() => {
    if (productsData?.body?.products) setProductsArray(productsData.body.products);
  }, [productsData]);

  useEffect(() => {
    if (productsByCategoryData?.body?.products) {
      setProductsArray(productsByCategoryData.body.products);
      if (productsByCategoryData.body.total_products) setTotalPages(Math.ceil(productsByCategoryData.body.total_products / limit));
    }
  }, [productsByCategoryData, limit]);

  useEffect(() => {
    if (countData?.body?.total_products) setTotalPages(Math.ceil(countData.body.total_products / limit));
  }, [countData, limit]);

  // ---- uploads ----
  const uploadFile = (event, mutation) => {
    const formData = new FormData();
    formData.append("image", event.target.files[0]);
    mutation({ body: formData });
  };

  useEffect(() => {
    if (uploadImageLoading) toast.loading("Uploading image...", { id: "up-img" });
    else toast.dismiss("up-img");
    if (uploadImageData) setImages(uploadImageData.body.fileLink);
    if (uploadImageError) toast.error("Image upload failed");
  }, [uploadImageData, uploadImageError, uploadImageLoading]);

  useEffect(() => {
    if (uploadPdfLoading) toast.loading("Uploading pdf...", { id: "up-pdf" });
    else toast.dismiss("up-pdf");
    if (uploadPdfData) setPdfFile(uploadPdfData.body.fileLink);
    if (uploadPdfError) toast.error("PDF upload failed");
  }, [uploadPdfData, uploadPdfError, uploadPdfLoading]);

  useEffect(() => {
    if (uploadDetailLoading) toast.loading("Uploading image...", { id: "up-det" });
    else toast.dismiss("up-det");
    if (uploadDetailData) setDetailImage(uploadDetailData.body.fileLink);
    if (uploadDetailError) toast.error("Detail image upload failed");
  }, [uploadDetailData, uploadDetailError, uploadDetailLoading]);

  // ---- create result ----
  useEffect(() => {
    if (addSchemeLoading) toast.loading("Creating scheme...", { id: "create-scheme" });
    else toast.dismiss("create-scheme");
    if (addSchemeData) {
      toast.success("Scheme created successfully");
      if (activeDraftId) deleteDraft(activeDraftId);
      navigate(`/genefied/${getVendorName()}/scheme`);
    }
    if (addSchemeError) toast.error(addSchemeError?.data?.message || "Failed to create scheme");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addSchemeData, addSchemeError, addSchemeLoading]);

  useEffect(() => {
    if (updateSchemeLoading) toast.loading("Updating scheme...", { id: "update-scheme" });
    else toast.dismiss("update-scheme");
    if (updateSchemeData) {
      toast.success("Scheme updated successfully");
      navigate(`/genefied/${getVendorName()}/scheme`);
    }
    if (updateSchemeError) toast.error(updateSchemeError?.data?.message || "Failed to update scheme");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateSchemeData, updateSchemeError, updateSchemeLoading]);

  // ---- product checkbox ----
  // User types whose source field is "sku_points" (they need the SKU-wise points sheet).
  const skuUserTypes = (applicableOn || [])
    .map((ut) => {
      const key = (ut?.user_type || ut?.name || "").toLowerCase().trim();
      const cfg = userTypePoints[key];
      return key && cfg?.enabled && cfg.key === "sku_points" ? { key, name: ut.name } : null;
    })
    .filter(Boolean);

  // Replaces the product selection. SKU-points rows for products that drop out are deleted,
  // after a confirmation.
  const applyProducts = (nextProducts) => {
    const keep = new Set(nextProducts.map((p) => p.product_code));
    const dropped = skuPointRows.filter((r) => !keep.has(r.product_code));
    if (dropped.length > 0) {
      const ok = window.confirm(
        `${dropped.length} product(s) being removed have SKU-wise points configured. Removing them will delete those points. Continue?`,
      );
      if (!ok) return false;
      setSkuPointRows(skuPointRows.filter((r) => keep.has(r.product_code)));
    }
    setSelectedProducts(nextProducts);
    return true;
  };

  const toggleProduct = (p) => {
    const exists = selectedProducts.some((x) => x.product_code === p.product_code);
    if (exists) applyProducts(selectedProducts.filter((x) => x.product_code !== p.product_code));
    else
      setSelectedProducts([
        ...selectedProducts,
        { product_id: p.product_id, product_code: p.product_code, name: p.name || p.product_name || "", category_id: p.category_id, category_name: p.category_name },
      ]);
  };

  const handleProductSearch = () => {
    if (searchQuery && searchQuery.length <= 2) {
      toast.error("Enter 3 characters or more to search", { id: "prodSearch" });
      return;
    }
    if (selectedCategories.id !== -1) fetchProductsByCatId(selectedCategories.id, searchQuery);
    else if (selectedClassification.id !== -1) fetchProductsByClassification(selectedClassification.name, searchQuery);
  };

  // ---- per-step validation ----
  const fail = (msg) => {
    toast.error(msg);
    return false;
  };

  const validateInfo = () => {
    if (!name) return fail("Name is required");
    if (!applicableOn || applicableOn.length <= 0) return fail("Select at least one user type");
    if (!startDate || !endDate) return fail("Scheme start and end dates are required");
    if (new Date(startDate) > new Date(endDate)) return fail("Scheme end must be after scheme start");
    return true;
  };

  const validateFilters = () => {
    if (!selectedStates || selectedStates.length <= 0) return fail("Select at least one state");
    if (schemeType.id === -1) return fail("Select a scheme type");
    // Products may stay empty here: when a user type uses SKU-wise points, the uploaded SKU
    // sheet (Points & Rewards step) defines the product list. validatePoints enforces it.
    return true;
  };

  const validatePoints = () => {
    if (!selectedGiftCatalogue || selectedGiftCatalogue.id === -1) return fail("Gift catalogue is required");
    if (schemeType.id !== 1 && selectedProducts.length <= 0 && skuPointRows.length <= 0) {
      return fail("Select at least one product (Filters & Products step) or upload SKU-wise points");
    }
    if (skuUserTypes.length > 0) {
      if (schemeType.id === 1) return fail("SKU-wise points can't be used with an All Products scheme — pick another scheme type");
      if (skuPointRows.length <= 0) return fail(`Upload SKU-wise points for ${skuUserTypes.map((u) => u.name).join(", ")}`);
    }
    // Parser is per selected user type; only enabled ones are validated/persisted.
    for (const ut of applicableOn) {
      const utKey = (ut?.user_type || ut?.name || "").toLowerCase().trim();
      const cfg = userTypePoints[utKey];
      if (utKey && cfg && cfg.enabled) {
        if (cfg.isCustom && !cfg.key?.trim()) return fail(`Context key name required for ${ut.name} (custom column)`);
        if (!cfg.parser || !cfg.parser.trim()) return fail(`Formula required for ${ut.name}`);
        const v = validateSchemeParserExpression(cfg.parser);
        if (!v.valid) return fail(`Invalid formula for ${ut.name}: ${v.error}`);
      }
    }
    return true;
  };

  const validateRedemption = () => {
    if (!redeemStart || !redeemEnd) return fail("Redeem start and end dates are required");
    if (new Date(redeemStart) > new Date(redeemEnd)) return fail("Redeem end must be after redeem start");
    if (new Date(startDate) > new Date(redeemStart)) return fail("Scheme start cannot be after redeem start");
    const reminderError = validateReminders(reminders, { endDate, redeemEnd });
    if (reminderError) return fail(reminderError);
    if (!redemptionEnabled) return true;

    const isPositive = (v) => v !== "" && v !== null && v !== undefined && Number(v) > 0;
    const requiredKeys = REDEMPTION_FIELD_KEYS.filter((k) => k !== "registration_bonus" || isPrimary);
    const missing = requiredKeys.filter((k) => !isPositive(redemptionDefault[k]));
    if (missing.length) {
      return fail(`Redemption defaults need a value greater than 0: ${missing.map((k) => REDEMPTION_FIELD_LABELS[k]).join(", ")}`);
    }
    // per-user-type overrides are partial (blank = inherit) but any value entered must be > 0
    for (const ut of applicableOn || []) {
      const utKey = ut?.user_type || ut?.name || "";
      const cfg = redemptionByUserType[utKey];
      if (!cfg?.enabled) continue;
      const bad = REDEMPTION_FIELD_KEYS.filter((k) => (isPrimary || k !== "registration_bonus") && cfg[k] !== "" && cfg[k] !== undefined && !isPositive(cfg[k]));
      if (bad.length) return fail(`${ut.name}: override values must be greater than 0 (${bad.map((k) => REDEMPTION_FIELD_LABELS[k]).join(", ")})`);
    }
    return true;
  };

  // step order: 0 Info · 1 Filters & Products · 2 Points & Rewards · 3 Redemption · 4 Review
  const validateStep = (step) => {
    if (step === 0) return validateInfo();
    if (step === 1) return validateFilters();
    if (step === 2) return validatePoints();
    if (step === 3) return validateRedemption();
    return true;
  };

  // ---- drafts ----
  const buildDraftData = () => ({
    name, applicableOn, selectedStates, startDate, endDate, redeemStart, redeemEnd,
    images, pdfFile, detailImage, description, isPrimary,
    selectedGiftCatalogue, selectedGiftTypes, selectedTierType, userTypePoints,
    tdsCalc, tdsRedeem, schemeUserCheck, reminders,
    redemptionEnabled, redemptionDefault, redemptionByUserType,
    schemeType, selectedClassification, selectedCategories, selectedProducts, skuPointRows,
  });

  const persistDraft = (stepForDraft = currentStep) => {
    if (isEdit) return; // drafts are a create-only convenience
    const hasContent = name || applicableOn.length || selectedProducts.length || selectedStates.length;
    if (!hasContent && !activeDraftId) return; // nothing worth saving yet
    const id = activeDraftId || Date.now();
    if (!activeDraftId) setActiveDraftId(id);
    const dName = draftName || (name ? name : `Untitled draft`);
    if (!draftName) setDraftName(dName);
    saveDraft({ id, name: dName, currentStep: stepForDraft, data: buildDraftData() });
    setLastSavedAt(Date.now());
    setDrafts(listDrafts());
  };

  const applyDraft = (d) => {
    const data = d.data || {};
    if ((data.schemeType?.id ?? -1) !== schemeType.id) applyingDraftRef.current = true;
    setName(data.name || "");
    setApplicableOn(data.applicableOn || []);
    setSelectedStates(data.selectedStates || []);
    setStartDate(data.startDate || getCurrentDate());
    setEndDate(data.endDate || "");
    setRedeemStart(data.redeemStart || getCurrentDate());
    setRedeemEnd(data.redeemEnd || "");
    setImages(data.images || null);
    setPdfFile(data.pdfFile || null);
    setDetailImage(data.detailImage || null);
    setDescription(data.description || "");
    setIsPrimary(!!data.isPrimary);
    setSelectedGiftCatalogue(data.selectedGiftCatalogue || { id: -1, name: "Select Catalogue" });
    setSelectedGiftTypes(data.selectedGiftTypes || []);
    setSelectedTierType(data.selectedTierType || TIER_TYPES[0]);
    setUserTypePoints(data.userTypePoints || {});
    setTdsCalc(!!data.tdsCalc);
    setTdsRedeem(data.tdsRedeem ?? true);
    setSchemeUserCheck(!!data.schemeUserCheck);
    setReminders({ ...emptyReminders(), ...(data.reminders || {}) });
    setRedemptionEnabled(!!data.redemptionEnabled);
    setRedemptionDefault(data.redemptionDefault || emptyRedemptionFields());
    setRedemptionByUserType(data.redemptionByUserType || {});
    setSchemeType(data.schemeType || { id: -1, name: "Select Type" });
    setSelectedClassification(data.selectedClassification || { id: -1, name: "Select Classification" });
    setSelectedCategories(data.selectedCategories || { id: -1, name: "Select Subcategory" });
    setSelectedProducts(data.selectedProducts || []);
    setSkuPointRows(data.skuPointRows || []);
    setActiveDraftId(d.id);
    setDraftName(d.name || "");
    setCurrentStep(d.currentStep || 0);
    setShowDrafts(false);
    toast.success("Draft loaded");
  };

  const removeDraft = (id) => {
    deleteDraft(id);
    setDrafts(listDrafts());
    if (id === activeDraftId) setActiveDraftId(null);
  };

  // ---- navigation ----
  const goToStep = (target) => {
    persistDraft(target);
    setCurrentStep(target);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNext = () => {
    if (validateStep(currentStep)) goToStep(currentStep + 1);
  };
  const handleBack = () => goToStep(Math.max(0, currentStep - 1));

  // Backend only ever credits registration_bonus for the tenant's primary scheme
  // (awardRegistrationBonusPrimaryScheme in crons/common/common.ts) — strip it here
  // so a non-primary scheme never persists a bonus that can never be paid out,
  // regardless of what's sitting in the (disabled) form state.
  const stripBonusUnlessPrimary = (fields) => {
    if (isPrimary) return fields;
    const { registration_bonus, ...rest } = fields;
    return rest;
  };

  const buildRedemptionConfig = () => {
    if (!redemptionEnabled) return null;
    const config = { default: stripBonusUnlessPrimary(fieldsToConfig(redemptionDefault)) };
    const byUserType = {};
    (applicableOn || []).forEach((ut) => {
      const utKey = ut?.user_type || ut?.name || "";
      const cfg = redemptionByUserType[utKey];
      if (utKey && cfg?.enabled) {
        const overrideFields = stripBonusUnlessPrimary(fieldsToConfig(cfg));
        if (Object.keys(overrideFields).length > 0) byUserType[utKey] = overrideFields;
      }
    });
    if (Object.keys(byUserType).length > 0) config.by_user_type = byUserType;
    return config;
  };

  const buildPayload = () => ({
    name,
    user_types: applicableOn.map((u) => u.user_type_id),
    states: selectedStates.map((s) => s.name),
    products: selectedProducts.map((p) => p.product_code),
    type: schemeType.id,
    gift_catalogue_id: selectedGiftCatalogue?.id,
    gift_type: selectedGiftTypes.map((gt) => gt.id),
    image: images,
    pdf: pdfFile,
    description: description || null,
    detail_image: detailImage || null,
    scheme_start: startDate,
    scheme_end: endDate,
    redeem_start: redeemStart,
    redeem_end: redeemEnd,
    tier_type: selectedTierType?.id,
    is_primary: isPrimary,
    settings: buildSchemeSettings({
      existing: editScheme?.settings || {},
      tdsCalc,
      tdsRedeem,
      schemeUserCheck,
      applicableOn,
      userTypePoints,
      notifications: normalizeReminders(reminders),
    }),
    redemption_config: buildRedemptionConfig(),
    // Only sent when a user type uses SKU-wise points; the backend replaces the scheme's rows with this set.
    ...(skuUserTypes.length > 0 ? { sku_points: normalizeSkuRows(skuPointRows) } : {}),
  });

  const handleSubmit = () => {
    for (let s = 0; s <= 3; s++) {
      if (!validateStep(s)) {
        setCurrentStep(s);
        return;
      }
    }
    if (isEdit) {
      // Send only the keys that actually changed; the backend falls back to the
      // stored value for any key that's absent.
      const body = changedPayload();
      if (Object.keys(body).length === 0) {
        toast("No changes to update");
        return;
      }
      updateScheme({ token: token(), tenant_id: tenantId, body, id: editScheme.id });
    } else {
      addScheme({ token: token(), tenant_id: tenantId, body: buildPayload() });
    }
  };

  // ---- edit diff (review step) ----
  const userTypeName = (id) =>
    userTypeOptions.find((u) => String(u.user_type_id) === String(id))?.name || String(id);
  const catalogueName = (id) =>
    allGiftCatalogues.find((c) => String(c.id) === String(id))?.name || String(id ?? "—");
  const schemeTypeName = (id) =>
    ALL_SCHEME_TYPES.find((t) => String(t.id) === String(id))?.name || String(id);
  const tierName = (id) => TIER_TYPES.find((t) => String(t.id) === String(id))?.name || String(id);
  const fmtFormulas = (utp) => {
    const entries = Object.entries(utp || {}).sort((a, b) => a[0].localeCompare(b[0]));
    return entries.length === 0 ? "None" : entries.map(([k, v]) => `${k}: ${v.key} → ${v.parser}`).join("; ");
  };

  // Original scheme row → payload shape, so before/after diff one structure.
  const schemeToPayload = (s) => ({
    name: s.name || "",
    user_types: (s.user_types || []).map(Number),
    states: s.states || [],
    products: s.products || [],
    type: s.type,
    gift_catalogue_id: s.gift_catalogue_id,
    gift_type: s.gift_type || [],
    image: s.image || null,
    pdf: s.pdf || null,
    description: s.description || null,
    detail_image: s.detail_image || s.detailImage || null,
    scheme_start: startDay(s.scheme_start),
    scheme_end: endDay(s.scheme_end),
    redeem_start: startDay(s.redeem_start),
    redeem_end: endDay(s.redeem_end),
    is_primary: s.is_primary === true || s.is_primary === "true",
    tier_type: s.tier_type,
    settings: s.settings || {},
    redemption_config: s.redemption_config || null,
  });

  // Canonical form of a payload value for change detection. List fields compare
  // order-insensitively; objects (settings) compare with sorted keys; scalars by
  // string so "1" (from DB) and 1 (from the form) don't count as a change.
  const stableStringify = (v) => {
    if (Array.isArray(v)) return `[${v.map(stableStringify).join(",")}]`;
    if (v && typeof v === "object")
      return `{${Object.keys(v).sort().map((k) => JSON.stringify(k) + ":" + stableStringify(v[k])).join(",")}}`;
    return JSON.stringify(v);
  };
  const LIST_KEYS = ["user_types", "states", "products", "gift_type"];
  const canon = (key, v) => {
    if (LIST_KEYS.includes(key)) return JSON.stringify([...(v || [])].map(String).sort());
    if (v && typeof v === "object") return stableStringify(v);
    return String(v);
  };

  // Only the payload keys whose value differs from the stored scheme.
  const changedPayload = () => {
    const before = schemeToPayload(editScheme);
    const after = buildPayload();
    const diff = {};
    before.sku_points = normalizeSkuRows(originalSkuRowsRef.current);
    Object.keys(after).forEach((k) => {
      if (canon(k, after[k]) !== canon(k, before[k])) diff[k] = after[k];
    });
    return diff;
  };

  const reviewModel = (p) => [
    ["Name", p.name],
    ["User Types", [...(p.user_types || [])].map(userTypeName).sort().join(", ")],
    ["States", [...(p.states || [])].sort().join(", ")],
    ["Products", Number(p.type) === 1 ? "All products" : `${(p.products || []).length}: ${[...(p.products || [])].sort().join(", ")}`],
    ["Scheme Type", schemeTypeName(p.type)],
    ["Gift Catalogue", catalogueName(p.gift_catalogue_id)],
    ["Gift Types", [...(p.gift_type || [])].sort().join(", ")],
    ["Scheme Start", p.scheme_start],
    ["Scheme End", p.scheme_end],
    ["Redeem Start", p.redeem_start],
    ["Redeem End", p.redeem_end],
    ["Primary Scheme", p.is_primary ? "Yes" : "No"],
    ...(isSeedworks ? [["Tier Type", tierName(p.tier_type)]] : []),
    ["Description", p.description || "—"],
    ["Banner Image", p.image ? "Set" : "—"],
    ["Detail Image", p.detail_image ? "Set" : "—"],
    ["PDF", p.pdf ? "Set" : "—"],
    ["TDS Redemption", (p.settings?.tds_redeem ?? true) ? "On" : "Off"],
    ["TDS Calculation", (p.settings?.tds_calc ?? false) ? "On" : "Off"],
    ["Restrict to assigned", p.settings?.type === "scheme_id_check" ? "Yes" : "No"],
    ["Custom formulas", fmtFormulas(p.settings?.user_type_points)],
    ["Push reminders", formatReminders(p.settings?.notifications)],
    ["SKU-wise points", p.sku_points ? `${p.sku_points.length} SKU row(s)` : "—"],
    ["Redemption Config", fmtRedemptionConfig(p.redemption_config)],
  ];

  const computeChanges = () => {
    if (!isEdit) return [];
    const beforePayload = { ...schemeToPayload(editScheme) };
    if (Object.values(editScheme.settings?.user_type_points || {}).some((c) => c?.key === "sku_points")) {
      beforePayload.sku_points = normalizeSkuRows(originalSkuRowsRef.current);
    }
    const before = Object.fromEntries(reviewModel(beforePayload));
    const after = Object.fromEntries(reviewModel(buildPayload()));
    return Object.keys(after)
      .filter((label) => (before[label] ?? "") !== (after[label] ?? ""))
      .map((label) => ({ label, old: before[label], next: after[label] }));
  };

  const savedLabel = lastSavedAt ? `Saved ${new Date(lastSavedAt).toLocaleTimeString()}` : "";

  return (
    <div className="min-h-screen w-full bg-gray-50 flex flex-col">
      {/* top bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="w-full px-6 pt-4 pb-3 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{isEdit ? "Update Scheme" : "Create Scheme"}</h1>
            <LinksComponent />
          </div>
          {!isEdit && (
            <div className="flex items-center gap-2">
              {savedLabel && (
                <span className="hidden sm:inline-flex items-center gap-1 text-xs text-green-600">
                  <HiCheck className="h-3.5 w-3.5" /> {savedLabel}
                </span>
              )}
              <button onClick={() => persistDraft()} className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50 transition-colors">
                Save draft
              </button>
              <button onClick={() => { setDrafts(listDrafts()); setShowDrafts(true); }} className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-sm hover:bg-gray-800 transition-colors">
                Drafts <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-white/20 text-[11px]">{drafts.length}</span>
              </button>
            </div>
          )}
        </div>
        <div className="w-full px-6 pb-4">
          <WizardStepper steps={STEPS} current={currentStep} onStep={(i) => i <= currentStep && goToStep(i)} />
        </div>
      </div>

      {/* content */}
      <div className="w-full px-6 py-6 flex-1">
        {currentStep === 0 && (
          <SectionCard title="Basic Info" desc={STEPS[0].desc}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <InputComponent name="Name" type="text" placeholder="Scheme name" value={name} setValue={setName} required />
              <MultiSelectComponent key={`ut-${editSelectNonce}`} by="id" title="User Types" selected={applicableOn} setSelected={setApplicableOn} dataArr={userTypeOptions} placeholder="Select user types" />
              <DateField label="Scheme Start" value={startDate} onChange={setStartDate} required />
              <DateField label="Scheme End" value={endDate} onChange={setEndDate} required />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-6">
              <FileDrop label="Banner Image" accept="image/*" kind="image" value={images} onChange={(e) => uploadFile(e, uploadImage)} />
              <FileDrop label="Detail Image" accept="image/*" kind="image" value={detailImage} onChange={(e) => uploadFile(e, uploadDetail)} />
              <FileDrop label="PDF" accept="application/pdf" kind="pdf" value={pdfFile} onChange={(e) => uploadFile(e, uploadPdf)} />
            </div>

            <div className="mt-6">
              <InputComponent name="Description" type="textarea" rows={3} placeholder="Optional description shown to users" value={description} setValue={setDescription} />
            </div>

            <div className="mt-6 rounded-xl bg-amber-50 border border-amber-100 p-4">
              <ToggleSwitch label="Primary Scheme" checked={isPrimary} onChange={setIsPrimary} hint="Used for automated onboarding & bonus point distribution. Only one active primary per date range." />
            </div>
          </SectionCard>
        )}

        {currentStep === 1 && (
          <SectionCard title="Filters & Products" desc={STEPS[1].desc}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <StateMultiSelect options={allStates} selected={selectedStates} setSelected={setSelectedStates} />
              <SelectComponentWithSearch title="Scheme Type" selected={schemeType} setSelected={setSchemeType} dataArr={ALL_SCHEME_TYPES} placeholder="Select type" required />
            </div>

            {schemeType.id === 2 && (
              <div className="mt-5">
                <SelectComponentWithSearch title="Classification" selected={selectedClassification} setSelected={setSelectedClassification} dataArr={classificationOptions} placeholder="Select classification" />
              </div>
            )}
            {schemeType.id === 3 && (
              <div className="mt-5">
                <SelectComponentWithSearch title="Sub Category" selected={selectedCategories} setSelected={setSelectedCategories} dataArr={subcategoryOptions} placeholder="Select sub category" />
              </div>
            )}

            {schemeType.id === 1 && (
              <div className="mt-5 rounded-xl bg-blue-50 border border-blue-100 p-4 text-sm text-blue-800">
                This scheme applies to <strong>all products</strong> — no product selection needed.
              </div>
            )}

            {schemeType.id !== 1 && schemeType.id !== -1 && (
              <div className="mt-5">
                <ProductCodeUpload selectedProducts={selectedProducts} applyProducts={applyProducts} />
                <div className="flex items-center gap-2 mb-3">
                  <input className={inputClass} placeholder="Search product code..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                  <button onClick={handleProductSearch} className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg whitespace-nowrap text-sm font-medium">Search</button>
                </div>

                <div className="border border-gray-200 rounded-xl max-h-72 overflow-auto divide-y divide-gray-100">
                  {productsArray.length === 0 ? (
                    <p className="text-sm text-gray-500 p-4">No products — pick a {schemeType.id === 2 ? "classification" : "sub category"} above, then search.</p>
                  ) : (
                    productsArray.map((p) => {
                      const checked = selectedProducts.some((x) => x.product_code === p.product_code);
                      return (
                        <label key={p.product_id} className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${checked ? "bg-blue-50/60" : "hover:bg-gray-50"}`}>
                          <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-blue-600" checked={checked} onChange={() => toggleProduct(p)} />
                          <span className="text-sm text-gray-700">
                            <span className="font-medium">{p.product_code}</span>
                            {(p.name || p.product_name) ? ` — ${p.name || p.product_name}` : ""}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-4 mt-3 text-sm text-gray-600">
                    <button disabled={currentPage <= 0} onClick={() => { const pg = currentPage - 1; setCurrentPage(pg); setOffset(pg * limit); }} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40"><MdKeyboardDoubleArrowLeft /></button>
                    <span>Page {currentPage + 1} of {totalPages}</span>
                    <button disabled={currentPage + 1 >= totalPages} onClick={() => { const pg = currentPage + 1; setCurrentPage(pg); setOffset(pg * limit); }} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40"><MdKeyboardDoubleArrowRight /></button>
                  </div>
                )}

                <p className="text-xs text-gray-500 mt-3">
                  {selectedProducts.length} product(s) selected
                  {selectedProducts.length === 0 && " — or skip this and upload SKU-wise points in the Points & Rewards step"}
                </p>
              </div>
            )}

            <div className="mt-6 rounded-xl bg-gray-50 border border-gray-200 p-4">
              <ToggleSwitch
                label='Restrict to assigned users only (scheme_id_check)'
                checked={schemeUserCheck}
                onChange={setSchemeUserCheck}
                hint="When enabled, only app users explicitly assigned to this scheme can scan."
              />
            </div>
          </SectionCard>
        )}

        {currentStep === 2 && (
          <SectionCard title="Points & Rewards" desc={STEPS[2].desc}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <SelectComponentWithSearch title="Gift Catalogue" selected={selectedGiftCatalogue} setSelected={setSelectedGiftCatalogue} dataArr={allGiftCatalogues} placeholder="Select catalogue" />
              <MultiSelectComponent key={`gt-${editSelectNonce}`} by="id" title="Gift Type" selected={selectedGiftTypes} setSelected={setSelectedGiftTypes} dataArr={GIFT_TYPE_OPTIONS} placeholder="Select gift type(s)" required={false} />
              {isSeedworks && (
                <SelectComponentWithSearch title="Tier Type" selected={selectedTierType} setSelected={setSelectedTierType} dataArr={TIER_TYPES} placeholder="Select tier type" />
              )}
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-800">Custom point formulas</h3>
                <span className="text-xs text-gray-400">applied per user type</span>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Base points come from your company-level point-sharing config. Optionally override the points for a specific user type with a formula below — each selected user type can have its own.
              </p>

              <div className="space-y-3">
                {(applicableOn || []).length === 0 && (
                  <div className="rounded-xl border border-dashed border-gray-300 p-4 text-sm text-gray-500">
                    Select user types in the Info step to configure per-user-type formulas.
                  </div>
                )}
                {(applicableOn || []).map((ut) => {
                  const utKey = (ut?.user_type || ut?.name || "").toLowerCase().trim();
                  const cfg = userTypePoints[utKey] || { enabled: false, key: "qr_mrp", parser: "", isCustom: false, db_column: "", db_table: "products" };
                  const setCfg = (patch) => setUserTypePoints({ ...userTypePoints, [utKey]: { ...cfg, ...patch } });

                  const selectValue = cfg.isCustom ? "__custom__" : (cfg.key || "qr_mrp");

                  return (
                    <div key={utKey} className="rounded-xl border border-gray-200 p-4">
                      {/* header */}
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold px-2.5 py-0.5 capitalize border border-indigo-100">{ut.name}</span>
                          <span className="text-sm text-gray-500">custom points formula</span>
                        </div>
                        <ToggleSwitch label="Enable" checked={cfg.enabled} onChange={(v) => setCfg({ enabled: v })} />
                      </div>

                      {cfg.enabled && (
                        <div className="mt-4 space-y-4">
                          {/* key selector */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Source field <span className="text-xs font-normal text-gray-400">(the value <code className="bg-gray-100 px-1 rounded">key</code> refers to in the formula)</span>
                            </label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                              {KEY_OPTIONS.map((opt) => {
                                const active = selectValue === opt.value;
                                const blocked = opt.needsSpecificProducts && schemeType.id === 1;
                                return (
                                  <button
                                    key={opt.value}
                                    type="button"
                                    disabled={blocked}
                                    title={blocked ? "Not available for All Products schemes — choose Classification or Sub Category in the Filters step" : undefined}
                                    onClick={() => {
                                      if (opt.value === "__custom__") {
                                        setCfg({ isCustom: true, key: cfg.isCustom ? cfg.key : "", db_column: cfg.db_column || "", db_table: cfg.db_table || "products" });
                                      } else {
                                        setCfg({ isCustom: false, key: opt.value, db_column: "", db_table: "products", ...(opt.value === "sku_points" && !cfg.parser?.trim() ? { parser: "key" } : {}) });
                                      }
                                    }}
                                    className={`text-left px-3 py-2 rounded-xl border text-xs transition-colors ${blocked ? "opacity-40 cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400" : ""} ${active ? "border-blue-500 bg-blue-50 text-blue-800 ring-1 ring-blue-500/30" : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"}`}
                                  >
                                    <div className="font-semibold mb-0.5">{opt.label}</div>
                                    <div className="text-[10px] text-gray-400 font-mono truncate">{opt.desc}</div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {/* custom column fields — only when "Custom column…" is selected */}
                          {cfg.isCustom && (
                            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
                              <p className="text-xs text-amber-700 font-medium">Custom column — fetched dynamically and merged into the formula context.</p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Context key <span className="text-gray-400">(name used in formula as <code className="bg-white px-0.5 rounded">key</code>)</span>
                                  </label>
                                  <input
                                    className={inputClass + " text-sm font-mono"}
                                    placeholder="e.g. wholesale_mrp"
                                    value={cfg.key}
                                    onChange={(e) => setCfg({ key: e.target.value.toLowerCase().replace(/\s+/g, "_") })}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    DB column name <span className="text-gray-400">(leave blank if same as context key)</span>
                                  </label>
                                  <input
                                    className={inputClass + " text-sm font-mono"}
                                    placeholder={cfg.key || "column_name"}
                                    value={cfg.db_column}
                                    onChange={(e) => setCfg({ db_column: e.target.value.toLowerCase().replace(/\s+/g, "_") })}
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">DB table</label>
                                <div className="flex gap-2">
                                  {["products", "qr"].map((tbl) => (
                                    <button
                                      key={tbl}
                                      type="button"
                                      onClick={() => setCfg({ db_table: tbl })}
                                      className={`px-4 py-1.5 rounded-lg border text-xs font-medium transition-colors ${(cfg.db_table || "products") === tbl ? "border-blue-500 bg-blue-50 text-blue-700" : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"}`}
                                    >
                                      {tbl}
                                    </button>
                                  ))}
                                </div>
                                <p className="text-[10px] text-gray-400 mt-1">
                                  Query: <code className="font-mono">{cfg.db_table || "products"}.{cfg.db_column || cfg.key || "column"} as {cfg.key || "key"}</code>
                                </p>
                              </div>
                            </div>
                          )}

                          {/* formula builder */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Formula</label>
                            <FormulaBuilder
                              parser={cfg.parser}
                              onChange={(v) => setCfg({ parser: v })}
                              utName={ut.name}
                            />
                            {cfg.key === "sku_points" && (
                              <p className="text-xs text-gray-500 mt-2">
                                Here <code className="bg-gray-100 px-1 rounded">key</code> is the points entered for each SKU in the SKU-wise points sheet below.
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {skuUserTypes.length > 0 && schemeType.id !== 1 && (
                <SkuPointsPanel
                  skuUserTypes={skuUserTypes}
                  selectedProducts={selectedProducts}
                  applyProducts={applyProducts}
                  rows={skuPointRows}
                  setRows={setSkuPointRows}
                />
              )}
            </div>
          </SectionCard>
        )}

        {currentStep === 3 && (
          <SectionCard title="Redemption" desc={STEPS[3].desc}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <DateField label="Redeem Start" value={redeemStart} onChange={setRedeemStart} required />
              <DateField label="Redeem End" value={redeemEnd} onChange={setRedeemEnd} required />
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-gray-200 p-4">
                <ToggleSwitch label="TDS Redemption" checked={tdsRedeem} onChange={(v) => { setTdsRedeem(v); if (v) setTdsCalc(false); }} hint="Deduct TDS as per user's PAN status on scheme redemptions." />
              </div>
              <div className="rounded-xl border border-gray-200 p-4">
                <ToggleSwitch label="TDS Calculation" checked={tdsCalc} onChange={(v) => { setTdsCalc(v); if (v) setTdsRedeem(false); }} hint="A manual button appears when the scheme ends to calculate TDS reserve points by PAN status." />
              </div>
            </div>

            <div className="mt-6 rounded-xl border border-gray-200 p-4">
              <ToggleSwitch
                label="Configure redemption for this scheme"
                checked={redemptionEnabled}
                onChange={setRedemptionEnabled}
                hint="Conversion rate, min/max limits and registration bonus — independent of the global App User Type cashPerPoint config. Cash and gift redemption stay blocked for this scheme until this is set."
              />

              {redemptionEnabled && (
                <div className="mt-5 space-y-6">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 mb-1">Default redemption settings</h3>
                    <p className="text-xs text-gray-500 mb-3">Applies to every eligible user type in this scheme, unless overridden below.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <NumberField
                        label="Conversion Rate (₹ per point)"
                        required
                        positive
                        value={redemptionDefault.cash_per_point}
                        onChange={(v) => setRedemptionDefault((prev) => applyAutoMinCash(prev, "cash_per_point", v))}
                      />
                      <NumberField
                        label="Minimum Points per Redemption"
                        required
                        positive
                        value={redemptionDefault.min_point_redeem}
                        onChange={(v) => setRedemptionDefault((prev) => applyAutoMinCash(prev, "min_point_redeem", v))}
                      />
                      <NumberField
                        label="Minimum Cash per Redemption"
                        required
                        positive
                        value={redemptionDefault.min_cash_redeem}
                        onChange={(v) => setRedemptionDefault((prev) => ({ ...prev, min_cash_redeem: v }))}
                        hint="Auto-filled as points × rate; edit to override."
                      />
                      <NumberField
                        label="Max Amount per Transaction (₹)"
                        required
                        positive
                        value={redemptionDefault.max_amount_per_transaction}
                        onChange={(v) => setRedemptionDefault((prev) => ({ ...prev, max_amount_per_transaction: v }))}
                      />
                      <NumberField
                        label="Max Amount per Day (₹)"
                        required
                        positive
                        value={redemptionDefault.max_amount_per_day}
                        onChange={(v) => setRedemptionDefault((prev) => ({ ...prev, max_amount_per_day: v }))}
                      />
                      <NumberField
                        label="Max Transactions per Day"
                        required
                        positive
                        value={redemptionDefault.max_transaction_per_day}
                        onChange={(v) => setRedemptionDefault((prev) => ({ ...prev, max_transaction_per_day: v }))}
                      />
                      <NumberField
                        label="Registration Bonus (points)"
                        required={isPrimary}
                        positive
                        disabled={!isPrimary}
                        value={redemptionDefault.registration_bonus}
                        onChange={(v) => setRedemptionDefault((prev) => ({ ...prev, registration_bonus: v }))}
                        hint={isPrimary ? "Credited once, on registration." : "Only the primary scheme can have a registration bonus — mark this scheme Primary in the Info step to set one."}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-800">Per-user-type overrides</h3>
                      <span className="text-xs text-gray-400">optional</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      Override any of the default values above for a specific user type. Leave a field blank to inherit the default.
                    </p>
                    <div className="space-y-3">
                      {(applicableOn || []).length === 0 && (
                        <div className="rounded-xl border border-dashed border-gray-300 p-4 text-sm text-gray-500">
                          Select user types in the Info step to configure per-user-type overrides.
                        </div>
                      )}
                      {(applicableOn || []).map((ut) => {
                        const utKey = ut?.user_type || ut?.name || "";
                        const cfg = redemptionByUserType[utKey] || { enabled: false, ...emptyRedemptionFields() };
                        const setCfg = (patch) => setRedemptionByUserType({ ...redemptionByUserType, [utKey]: { ...cfg, ...patch } });
                        return (
                          <div key={utKey} className="rounded-xl border border-gray-200 p-4">
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold px-2.5 py-0.5 capitalize border border-indigo-100">{ut.name}</span>
                                <span className="text-sm text-gray-500">override</span>
                              </div>
                              <ToggleSwitch label="Enable" checked={cfg.enabled} onChange={(v) => setCfg({ enabled: v })} />
                            </div>
                            {cfg.enabled && (
                              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                <NumberField positive label="Conversion Rate (₹ per point)" value={cfg.cash_per_point} placeholder={redemptionDefault.cash_per_point ? `Default: ${redemptionDefault.cash_per_point}` : "Default"} onChange={(v) => setCfg(applyAutoMinCash(cfg, "cash_per_point", v))} />
                                <NumberField positive label="Minimum Points per Redemption" value={cfg.min_point_redeem} placeholder={redemptionDefault.min_point_redeem ? `Default: ${redemptionDefault.min_point_redeem}` : "Default"} onChange={(v) => setCfg(applyAutoMinCash(cfg, "min_point_redeem", v))} />
                                <NumberField positive label="Minimum Cash per Redemption" value={cfg.min_cash_redeem} placeholder={redemptionDefault.min_cash_redeem ? `Default: ${redemptionDefault.min_cash_redeem}` : "Default"} onChange={(v) => setCfg({ min_cash_redeem: v })} />
                                <NumberField positive label="Max Amount per Transaction (₹)" value={cfg.max_amount_per_transaction} placeholder={redemptionDefault.max_amount_per_transaction ? `Default: ${redemptionDefault.max_amount_per_transaction}` : "Default"} onChange={(v) => setCfg({ max_amount_per_transaction: v })} />
                                <NumberField positive label="Max Amount per Day (₹)" value={cfg.max_amount_per_day} placeholder={redemptionDefault.max_amount_per_day ? `Default: ${redemptionDefault.max_amount_per_day}` : "Default"} onChange={(v) => setCfg({ max_amount_per_day: v })} />
                                <NumberField positive label="Max Transactions per Day" value={cfg.max_transaction_per_day} placeholder={redemptionDefault.max_transaction_per_day ? `Default: ${redemptionDefault.max_transaction_per_day}` : "Default"} onChange={(v) => setCfg({ max_transaction_per_day: v })} />
                                <NumberField positive label="Registration Bonus (points)" disabled={!isPrimary} value={cfg.registration_bonus} placeholder={redemptionDefault.registration_bonus ? `Default: ${redemptionDefault.registration_bonus}` : "Default"} hint={!isPrimary ? "Only the primary scheme can have a registration bonus." : undefined} onChange={(v) => setCfg({ registration_bonus: v })} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <SchemeReminders value={reminders} onChange={setReminders} endDate={endDate} redeemEnd={redeemEnd} />
          </SectionCard>
        )}

        {currentStep === 4 && (
          <SectionCard title={isEdit ? "Review & Update" : "Review & Create"} desc={STEPS[4].desc}>
            {isEdit && <ChangesPanel changes={computeChanges()} />}
            <ReviewGrid
              rows={[
                ["Name", name],
                ["User Types", applicableOn.map((u) => u.name).join(", ")],
                ["Scheme Window", `${startDate} → ${endDate}`],
                ["Redeem Window", `${redeemStart} → ${redeemEnd}`],
                ["Gift Catalogue", selectedGiftCatalogue?.name],
                ["Gift Types", selectedGiftTypes.map((g) => g.name).join(", ")],
                ...(isSeedworks ? [["Tier Type", selectedTierType?.name]] : []),
                ["Scheme Type", schemeType?.name],
                ["Products", schemeType.id === 1 ? "All products (no product codes stored)" : `${selectedProducts.length} selected`],
                ["Primary Scheme", isPrimary ? "Yes" : "No"],
                ["TDS Redemption", tdsRedeem ? "On" : "Off"],
                ["TDS Calculation", tdsCalc ? "On" : "Off"],
                ["Restrict to assigned", schemeUserCheck ? "Yes" : "No"],
                ["Banner Image", images ? "Uploaded" : "—"],
                ["Detail Image", detailImage ? "Uploaded" : "—"],
                ["PDF", pdfFile ? "Uploaded" : "—"],
              ]}
            />

            {description && (
              <ReviewSection title="Description">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{description}</p>
              </ReviewSection>
            )}

            <ReviewSection title={`States (${selectedStates.length})`}>
              {selectedStates.length === 0 ? (
                <span className="text-sm text-gray-400">None selected</span>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {selectedStates.map((s) => <Chip key={s.id}>{s.name}</Chip>)}
                </div>
              )}
            </ReviewSection>

            {schemeType.id !== 1 && (
              <ReviewSection title={`Products (${selectedProducts.length})`}>
                {selectedProducts.length === 0 ? (
                  <span className="text-sm text-gray-400">None selected</span>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {selectedProducts.map((p) => <Chip key={p.product_id}>{p.product_code}</Chip>)}
                  </div>
                )}
              </ReviewSection>
            )}

            <ReviewSection title="Custom point formulas">
              {(() => {
                const formulas = (applicableOn || [])
                  .map((ut) => {
                    const utKey = (ut?.user_type || ut?.name || "").toLowerCase().trim();
                    const cfg = userTypePoints[utKey];
                    return cfg && cfg.enabled ? { name: ut.name, key: cfg.key, parser: cfg.parser, isCustom: cfg.isCustom, db_column: cfg.db_column, db_table: cfg.db_table } : null;
                  })
                  .filter(Boolean);
                if (formulas.length === 0)
                  return <span className="text-sm text-gray-400">None — base company points apply to all user types.</span>;
                return (
                  <div className="space-y-2">
                    {formulas.map((f) => (
                      <div key={f.name} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="inline-flex items-center rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold px-2.5 py-0.5 capitalize border border-indigo-100">{f.name}</span>
                          <span className="text-gray-400 text-xs">key =</span>
                          <code className="bg-white border border-gray-200 px-1.5 py-0.5 rounded text-xs font-mono">
                            {f.isCustom ? f.key : (KEY_OPTIONS.find((k) => k.value === f.key)?.label || f.key)}
                          </code>
                          <span className="text-gray-400">→</span>
                          <code className="bg-white border border-gray-200 px-1.5 py-0.5 rounded text-xs font-mono">{f.parser}</code>
                        </div>
                        {f.isCustom && (
                          <p className="text-[11px] text-amber-700 font-mono">
                            DB: <span className="bg-amber-100 px-1 rounded">{f.db_table || "products"}.{f.db_column || f.key}</span>
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </ReviewSection>

            {skuUserTypes.length > 0 && (
              <ReviewSection title={`SKU-wise points (${skuPointRows.length} SKUs)`}>
                <div className="flex flex-wrap gap-1.5">
                  {skuUserTypes.map((u) => (
                    <Chip key={u.key}>
                      {u.name}: {skuPointRows.filter((r) => r.points?.[u.key] !== undefined && r.points[u.key] !== "").length} value(s)
                    </Chip>
                  ))}
                </div>
              </ReviewSection>
            )}

            <ReviewSection title="Redemption limits (cashPerPoint)">
              {(() => {
                // Built from the same function the submit uses, so Review always shows exactly
                // what will be saved (e.g. registration_bonus silently dropped when not Primary).
                const finalConfig = buildRedemptionConfig();
                if (!finalConfig) {
                  return <span className="text-sm text-amber-600">Not configured — cash and gift redemption will be blocked for this scheme.</span>;
                }
                const defaultFields = finalConfig.default || {};
                const overrides = finalConfig.by_user_type || {};
                return (
                  <div className="space-y-3">
                    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5">
                      <div className="text-xs font-semibold text-gray-500 mb-1.5">Default</div>
                      <div className="flex flex-wrap gap-1.5">
                        {REDEMPTION_FIELD_KEYS.filter((k) => defaultFields[k]).map((k) => (
                          <Chip key={k}>{REDEMPTION_FIELD_LABELS[k]}: {defaultFields[k]}</Chip>
                        ))}
                        {REDEMPTION_FIELD_KEYS.every((k) => !defaultFields[k]) && (
                          <span className="text-sm text-gray-400">No limits set</span>
                        )}
                      </div>
                      {redemptionDefault.registration_bonus && !isPrimary && (
                        <p className="text-[11px] text-amber-600 mt-1.5">
                          Registration bonus entered but not saved — only the primary scheme can have one.
                        </p>
                      )}
                    </div>
                    {Object.entries(overrides).map(([utKey, cfg]) => (
                      <div key={utKey} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="inline-flex items-center rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold px-2.5 py-0.5 capitalize border border-indigo-100">{utKey}</span>
                          <span className="text-xs text-gray-400">override</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {REDEMPTION_FIELD_KEYS.filter((k) => cfg[k]).map((k) => (
                            <Chip key={k}>{REDEMPTION_FIELD_LABELS[k]}: {cfg[k]}</Chip>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </ReviewSection>

            {(images || detailImage || pdfFile) && (
              <ReviewSection title="Media">
                <div className="flex flex-wrap items-center gap-3">
                  {images && <img src={images} alt="banner" className="h-32 w-32 rounded-lg border border-gray-200 object-cover" />}
                  {detailImage && <img src={detailImage} alt="detail" className="h-32 w-32 rounded-lg border border-gray-200 object-cover" />}
                  {pdfFile && (
                    <a href={pdfFile} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100 transition-colors">
                      <FiFileText className="h-4 w-4" /> View PDF
                    </a>
                  )}
                </div>
              </ReviewSection>
            )}
          </SectionCard>
        )}
      </div>

      {/* sticky footer nav */}
      <div className="sticky bottom-0 z-10 bg-white/90 backdrop-blur border-t border-gray-200">
        <div className="w-full px-6 py-3 flex items-center justify-between">
          <button onClick={handleBack} disabled={currentStep === 0} className="px-5 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-40 transition-colors">
            Back
          </button>
          <span className="text-xs text-gray-400 hidden sm:block">Step {currentStep + 1} of {STEPS.length}</span>
          {currentStep < STEPS.length - 1 ? (
            <button onClick={handleNext} className="px-6 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-sm transition-colors">
              Continue
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={addSchemeLoading || updateSchemeLoading} className="px-6 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold shadow-sm disabled:opacity-50 transition-colors">
              {isEdit
                ? updateSchemeLoading ? "Updating..." : "Update Scheme"
                : addSchemeLoading ? "Creating..." : "Create Scheme"}
            </button>
          )}
        </div>
      </div>

      <ModalComponent size="lg" show={showDrafts} setShow={setShowDrafts} showClose={true}>
        <div className="p-5">
          <h3 className="text-lg font-semibold mb-4">Saved Drafts</h3>
          {drafts.length === 0 ? (
            <p className="text-sm text-gray-500">No saved drafts yet.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-auto">
              {drafts.map((d) => (
                <div key={d.id} className="flex items-center justify-between border border-gray-200 rounded-xl px-4 py-3 hover:border-gray-300 transition-colors">
                  <div>
                    <div className="font-medium text-sm text-gray-800">{d.name || "Untitled draft"}</div>
                    <div className="text-xs text-gray-500 mt-0.5">Step {(d.currentStep || 0) + 1} of {STEPS.length} · {new Date(d.updatedAt).toLocaleString()}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => applyDraft(d)} className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700">Resume</button>
                    <button onClick={() => removeDraft(d.id)} className="px-3 py-1.5 text-sm rounded-lg bg-red-50 text-red-600 hover:bg-red-100">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </ModalComponent>
    </div>
  );
};

const ChangesPanel = ({ changes }) => (
  <div className="mb-6 rounded-xl border border-gray-200 overflow-hidden">
    <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
      <h4 className="text-sm font-semibold text-gray-800">Changes</h4>
      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${changes.length ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-500"}`}>
        {changes.length ? `${changes.length} field${changes.length > 1 ? "s" : ""} changed` : "No changes"}
      </span>
    </div>
    {changes.length === 0 ? (
      <p className="text-sm text-gray-500 px-4 py-3">Nothing has changed from the original scheme yet.</p>
    ) : (
      <div className="divide-y divide-gray-100">
        {changes.map((c) => (
          <div key={c.label} className="px-4 py-3">
            <div className="text-sm font-medium text-gray-800 mb-1">{c.label}</div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 text-sm">
              <span className="inline-flex items-center rounded bg-red-50 text-red-700 border border-red-100 px-2 py-0.5 line-through break-all">{c.old || "—"}</span>
              <span className="text-gray-400 hidden sm:inline">→</span>
              <span className="inline-flex items-center rounded bg-green-50 text-green-700 border border-green-100 px-2 py-0.5 break-all">{c.next || "—"}</span>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const ReviewGrid = ({ rows }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-1">
    {rows.map(([label, value]) => (
      <div key={label} className="flex justify-between gap-4 border-b border-gray-100 py-2.5">
        <span className="text-sm text-gray-500">{label}</span>
        <span className="text-sm font-medium text-gray-800 text-right capitalize">{value || "-"}</span>
      </div>
    ))}
  </div>
);

const ReviewSection = ({ title, children }) => (
  <div className="mt-6 pt-5 border-t border-gray-100">
    <h4 className="text-sm font-semibold text-gray-800 mb-2">{title}</h4>
    {children}
  </div>
);

const Chip = ({ children }) => (
  <span className="inline-flex items-center bg-gray-100 text-gray-700 text-xs px-2.5 py-1 rounded-full border border-gray-200 capitalize">
    {children}
  </span>
);

export default CreateSchemeWizard;
